import * as React from 'react';
import styled, { css } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { MessageDescriptor } from '@lingui/core';

import { ROUTE_PATHS } from '../../_pages/_utils/router.utils';
import { useUserContext } from '../../../providers/user/user.provider';

const linkStyles = css`
  position: relative;
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.gray8};
  text-decoration: none;
  padding: 8px 12px;
  margin: 0 8px;
`;

const NavLink = styled(Link)<{ active: 'true' | 'false' }>`
  ${linkStyles}
  color: ${({ active }) => (active === 'true' ? 'var(--primary)' : 'black')};
  background-color: ${({ active }) =>
    active === 'true' ? 'color-mix(in oklab, var(--primary) 5%, transparent)' : 'transparent'};
  border: ${({ active }) =>
    active === 'true' ? '1px solid color-mix(in oklab,var(--primary) 20%,transparent)' : 'none'};
  border-radius: 8px;
`;

const ExternalLink = styled('a')`
  ${linkStyles}
`;

interface NavItem {
  displayName: MessageDescriptor;
  link: string;
  type: 'internal' | 'external';
}

const items: NavItem[] = [
  {
    displayName: msg`Debts`,
    link: ROUTE_PATHS.MY_DEBTS,
    type: 'internal',
  },
  {
    displayName: msg`Credit Score`,
    link: ROUTE_PATHS.CREDIT_SCORE,
    type: 'internal',
  },
  {
    displayName: msg`Rent Reporting`,
    link: ROUTE_PATHS.RENT_REPORTING,
    type: 'internal',
  },
  { displayName: msg`Home`, link: ROUTE_PATHS.DASHBOARD, type: 'internal' },
];

const NavItems = ({ className }: { className?: string }) => {
  const { pathname } = useLocation();
  const { user } = useUserContext();
  const { i18n } = useLingui();
  const navItems = [...items];
  const handleNavClick = (isCurrentPath: boolean) => {
    if (isCurrentPath) {
      window.location.reload();
    }
  };
  if (user?.hasActiveFranklinLoan) {
    navItems.unshift({
      displayName: msg`Loans`,
      link: ROUTE_PATHS.LOANS_MANAGEMENT,
      type: 'internal',
    });
  }

  return (
    <div className={className}>
      {navItems.map(({ displayName, link, type }, index) => {
        const label = i18n._(displayName);
        if (type === 'internal') {
          let isActive = pathname === link;
          if (
            link === ROUTE_PATHS.LOANS_MANAGEMENT &&
            [ROUTE_PATHS.LOAN_APPLICATION, ROUTE_PATHS.CREDIT_BUILDER, ROUTE_PATHS.SHORT_TERM_LOAN].includes(pathname)
          ) {
            isActive = true;
          }

          return (
            <NavLink
              to={link}
              active={isActive ? 'true' : 'false'}
              key={`${label}-${index}`}
              onClick={() => handleNavClick(pathname === link)}
            >
              {label}
            </NavLink>
          );
        }

        return (
          <ExternalLink href={link} key={`${link}-${index}`} target="_blank" rel="noreferrer">
            {label}
          </ExternalLink>
        );
      })}
    </div>
  );
};

export default styled(NavItems)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 56px;
`;
